import React from "react";
import {
  DSRDashboardMetric,
  DSRDashboardMetricsResponse,
  dsrAuditsApi,
} from "../../../../../api/dsrAuditsApi";
import { ROUTES } from "../../../../../config/routes";
import { WidgetComponentProps } from "../../types";
import { useWidgetData } from "../../useWidgetData";
import {
  CardError,
  CardLoading,
  Freshness,
  Kpi,
  KpiGrid,
  ListRows,
  Tone,
  countTone,
  formatNumber,
} from "../../ui";
import { latestOf } from "./cardHelpers";

/**
 * Fallback for when the server omits `stale`; mirrors STALE_REPORT_THRESHOLD_DAYS
 * in server/src/services/dsrAuditsDashboardMetricsService.ts.
 */
const STALE_REPORT_DAYS = 31;

const REPORT_TYPES: Array<{ key: keyof DSRDashboardMetricsResponse; label: string; to: string }> = [
  { key: "genent", label: "GenEnt", to: ROUTES.DSR_GENENT },
  { key: "music-videos", label: "Music videos", to: ROUTES.DSR_MUSIC_VIDEOS },
  { key: "sound-recordings", label: "Sound recordings", to: ROUTES.DSR_SOUND_RECORDINGS },
];

const isStale = (m: DSRDashboardMetric | undefined): boolean =>
  !m ||
  (typeof m.stale === "boolean"
    ? m.stale
    : m.latestReportAgeDays == null || m.latestReportAgeDays > STALE_REPORT_DAYS);

const ageText = (m: DSRDashboardMetric | undefined): string =>
  m?.latestReportAgeDays == null
    ? "no report yet"
    : m.latestReportAgeDays === 0
      ? "report today"
      : `report ${m.latestReportAgeDays}d old`;

const MusicDsrCard: React.FC<WidgetComponentProps> = () => {
  const { data, loading, error, reload } = useWidgetData<DSRDashboardMetricsResponse>(
    "music-dsr",
    () => dsrAuditsApi.getDashboardMetrics()
  );

  if (loading && !data) return <CardLoading />;
  if (error) return <CardError message={error} onRetry={reload} />;
  if (!data) return null;

  const metrics = REPORT_TYPES.map((t) => ({ ...t, metric: data[t.key] as DSRDashboardMetric | undefined }));
  const pending = metrics.reduce((sum, m) => sum + (Number(m.metric?.pendingReviewCount) || 0), 0);
  const queueIssues = metrics.reduce((sum, m) => sum + (Number(m.metric?.latestQueueIssueRecords) || 0), 0);
  const staleCount = metrics.filter((m) => isStale(m.metric)).length;
  const firstPending = metrics.find((m) => (m.metric?.pendingReviewCount ?? 0) > 0);
  const firstIssue = metrics.find((m) => (m.metric?.latestQueueIssueRecords ?? 0) > 0);
  const firstStale = metrics.find((m) => isStale(m.metric));
  const staleTone: Tone = staleCount === 0 ? "good" : "warn";

  return (
    <>
      <KpiGrid columns={3}>
        <Kpi
          label="Pending reviews"
          value={pending}
          tone={countTone(pending)}
          to={(firstPending ?? metrics[0]).to}
          emphasis
        />
        <Kpi
          label="Queue issues"
          value={queueIssues}
          tone={countTone(queueIssues)}
          hint="Latest audit queues"
          to={(firstIssue ?? metrics[0]).to}
        />
        <Kpi
          label="Stale reports"
          value={staleCount}
          tone={staleTone}
          hint={`Older than ${STALE_REPORT_DAYS} days`}
          to={(firstStale ?? metrics[0]).to}
        />
      </KpiGrid>

      <ListRows
        title="Reports"
        rows={metrics.map((m) => ({
          key: m.key,
          primary: (
            <span className={isStale(m.metric) ? "home-text-warn" : undefined}>
              {isStale(m.metric) && <i className="pi pi-exclamation-triangle" />} {m.label}
            </span>
          ),
          secondary: `${ageText(m.metric)} · ${formatNumber(m.metric?.pendingReviewCount ?? 0)} pending · ${formatNumber(
            m.metric?.latestQueueIssueRecords ?? 0
          )} issues`,
          to: m.to,
        }))}
      />

      <div className="home-card__meta">
        <Freshness label="Latest report" at={latestOf(metrics.map((m) => m.metric?.latestReportAt))} />
      </div>
    </>
  );
};

export default MusicDsrCard;
